import { useCallback, useEffect } from 'react'
import { Card, Stack, Typography } from '@mui/material'
import Grid from '@mui/material/Grid2'
import useStatefulSearchParam from '@/hooks/useStatefulSearchParam'
import {
  useGetProjectDocumentRules,
  useUpdateProjectDocumentRule,
} from '@/service-library/hooks/project-document-rules'
import { useGetProjectGridFields } from '@/service-library/hooks/project-grid-fields'
import queryKeys from '@/service-library/query-keys'
import { getFieldsFromProject } from '@/utils/field'
import { showErrorSnackbar, showSuccessSnackbar } from '@/utils/snackbars'
import ProjectBreadcrumb from '@/components/project-tables/ProjectBreadcrumb'
import { useProjectContext } from '@/components/project-tables/ProjectProvider'
import RuleSettings, { FormValues } from '@/components/rules/RuleSettings'
import DocumentRules from './DocumentRules'

export default function DocumentRulesPage() {
  const { project } = useProjectContext()
  const allFields = getFieldsFromProject(project)

  const documentRulesQuery = useGetProjectDocumentRules({
    filters: {
      project_id: project.id,
      fields__include: 'type,workflow_states_ids,dependent_fields_ids',
      limit: '1000',
    },
    enabled: !!project,
  })

  const { projectGridFields } = useGetProjectGridFields({
    filters: {
      limit: '1000',
      fields__only: 'id,name,project_grid_field_type,full_name',
      project_id: project.id,
    },
    initialListData: allFields,
    staleTime: 1000,
    enabled: !!project,
  })

  const {
    value: selectedRuleId,
    isInUrl,
    toggleValue,
    resetValue,
  } = useStatefulSearchParam({
    param: 'rule_id',
  })

  const { updateProjectDocumentRuleAsync, isPending } =
    useUpdateProjectDocumentRule({
      sideEffectQueryKeys: [queryKeys.projectDocumentRules.details()],
      onSuccess: () => {
        showSuccessSnackbar('Document Rule Updated')
      },
      onError: () => {
        showErrorSnackbar('Document Rule Update Failed')
      },
    })

  const { projectDocumentRules, isPending: rulesIsLoading } = documentRulesQuery
  const selectedRule = projectDocumentRules.find(
    (rule) => rule.id === selectedRuleId,
  )

  const defaultRuleId = projectDocumentRules[0]?.id

  const setSelectedRuleId = useCallback(
    (ruleId: string | undefined) => {
      if (ruleId) {
        toggleValue(ruleId)
        return
      }

      if (defaultRuleId) {
        toggleValue(defaultRuleId)
      } else if (isInUrl) {
        resetValue()
      }
    },
    [defaultRuleId, isInUrl, resetValue, toggleValue],
  )

  const updateDocumentRule = async ({
    workflow_states_ids,
    run_in_all_workflow_states,
    dependent_fields,
    is_superuser_rule,
    ...rest
  }: FormValues) => {
    if (selectedRule) {
      return updateProjectDocumentRuleAsync({
        ...selectedRule,
        dependent_fields_ids: dependent_fields.map(({ id }) => id),
        workflow_states_ids,
        run_in_all_workflow_states,
        is_superuser_rule,
        params: rest,
      })
    }
    return
  }

  useEffect(() => {
    if (!rulesIsLoading && !selectedRule) {
      setSelectedRuleId(undefined)
    }
  }, [selectedRuleId, selectedRule, setSelectedRuleId, rulesIsLoading])

  return (
    <Stack spacing={2} sx={{ height: '100%' }}>
      <ProjectBreadcrumb label="Document Rules" url="../document-rules" />
      <Typography variant="h5" color="textPrimary">
        Document Rules
      </Typography>
      <Card sx={{ height: '100%', flexGrow: 1 }}>
        <Grid container spacing={2} sx={{ height: '100%', my: 0 }}>
          <Grid
            size={6}
            sx={{ height: '100%', overflow: 'auto', py: 2, pl: 3 }}
          >
            <DocumentRules
              documentRulesQuery={documentRulesQuery}
              selectedRuleId={selectedRuleId}
              setSelectedRuleId={setSelectedRuleId}
            />
          </Grid>
          {selectedRule && (
            <Grid size={6} sx={{ py: 2, pr: 3 }}>
              <RuleSettings
                key={selectedRule.id} // Completely rerender each time we change the selected rule
                isLoading={isPending}
                onSubmit={updateDocumentRule}
                rule={selectedRule}
                fields={projectGridFields}
              />
            </Grid>
          )}
        </Grid>
      </Card>
    </Stack>
  )
}
